import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ChoghadiyaSlot } from '@/lib/api'

const CHOGHADIYA_CFG: Record<string, { bg: string; text: string; border: string }> = {
  Amrit: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/25' },
  Shubh: { bg: 'bg-amber-500/15',  text: 'text-amber-400',   border: 'border-amber-500/25'  },
  Labh:  { bg: 'bg-green-500/15',  text: 'text-green-400',   border: 'border-green-500/25'  },
  Char:  { bg: 'bg-blue-500/10',   text: 'text-blue-400',    border: 'border-blue-500/20'   },
  Udveg: { bg: 'bg-cosmic-dusk/60', text: 'text-star-muted',  border: 'border-cosmic-mist'   },
  Kaal:  { bg: 'bg-red-500/10',    text: 'text-red-400',     border: 'border-red-500/20'    },
  Rog:   { bg: 'bg-red-500/10',    text: 'text-red-400',     border: 'border-red-500/20'    },
}

const FAVORABLE = new Set(['Amrit', 'Shubh', 'Labh'])

interface Props {
  choghadiyaSlots: ChoghadiyaSlot[]
  inauspiciousPeriods: Array<{ name: string; start: string; end: string }>
  narrative: string
}

/**
 * Compact 1/3-column forecast panel for the dashboard.
 * Shows: current Choghadiya slot, next favorable window, avoid periods,
 * and the dasha daily guidance narrative.
 */
export function QuickForecast({ choghadiyaSlots, inauspiciousPeriods, narrative }: Props) {
  const now      = dayjs()
  const daySlots = choghadiyaSlots.filter(s => s.period === 'day')

  const activeSlot    = daySlots.find(s =>
    now.isAfter(dayjs(s.startTime)) && now.isBefore(dayjs(s.endTime)),
  )
  const nextFavorable = daySlots.find(s =>
    FAVORABLE.has(s.quality) && now.isBefore(dayjs(s.startTime)),
  )

  return (
    <div className="space-y-3 h-full flex flex-col">
      {/* Current Choghadiya */}
      {activeSlot && (() => {
        const cfg = CHOGHADIYA_CFG[activeSlot.quality] ?? CHOGHADIYA_CFG.Udveg
        return (
          <div className={cn('p-3 rounded-lg border', cfg.bg, cfg.border)}>
            <p className="text-star-dim text-2xs font-mono uppercase tracking-wider mb-1.5">Now</p>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
              <span className={cn('text-sm font-semibold', cfg.text)}>{activeSlot.quality}</span>
              <span className="text-star-dim text-2xs font-mono ml-auto tabular-nums">
                –{dayjs(activeSlot.endTime).format('HH:mm')}
              </span>
            </div>
            {activeSlot.planet && (
              <p className="text-star-dim text-2xs font-mono mt-0.5">{activeSlot.planet}</p>
            )}
          </div>
        )
      })()}

      {/* Next favorable */}
      {nextFavorable && (
        <div className="p-3 rounded-lg bg-cosmic-void/50 ring-1 ring-white/5">
          <p className="text-star-dim text-2xs font-mono uppercase tracking-wider mb-1.5">Next Favorable</p>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span className="text-emerald-400 text-xs font-semibold">{nextFavorable.quality}</span>
            <span className="text-star-dim text-2xs font-mono ml-auto tabular-nums">
              {dayjs(nextFavorable.startTime).format('HH:mm')}
            </span>
          </div>
        </div>
      )}

      {/* Avoid periods */}
      {inauspiciousPeriods.length > 0 && (
        <div className="p-3 rounded-lg bg-cosmic-void/50 ring-1 ring-white/5 space-y-1.5">
          <p className="text-star-dim text-2xs font-mono uppercase tracking-wider">Avoid</p>
          {inauspiciousPeriods.map(p => (
            <div key={p.name} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400/70 flex-shrink-0" />
              <span className="text-red-400/80 text-xs font-medium">{p.name}</span>
              <span className="text-star-dim font-mono ml-auto text-2xs tabular-nums">
                {dayjs(p.start).format('HH:mm')}–{dayjs(p.end).format('HH:mm')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Daily narrative */}
      {narrative && (
        <div className="p-3 rounded-lg bg-cosmic-void/50 ring-1 ring-white/5 flex-1">
          <p className="text-star-dim text-2xs font-mono uppercase tracking-wider mb-1.5">Daily Guidance</p>
          <p className="text-star-soft text-xs leading-relaxed font-display italic line-clamp-5">
            "{narrative}"
          </p>
        </div>
      )}

      <Link
        to="/daily-timings"
        className="flex items-center gap-1 text-amber-400/60 text-2xs hover:text-amber-400 transition-colors mt-auto"
      >
        Full Choghadiya <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  )
}
